import _ from 'lodash'

import {
    getParameterByName,
    EnvironmentDetail,
    UUIDUtils,
    LatencyMetric,
    CountMetric,
} from '@amzn/ihm-associate-tools-js-lib'
import { IIBTokenProvider } from './utils/IIBTokenProvider'
import { StoreIdService } from '@pages/StoreIdSelection/utils/StoreIdService'
import { UserTypeService } from '@src/pages/UserTypeSelection/utils/UserTypeService'
import { DorothyUtils } from '@utils/DorothyUtils'
import { store } from '@atredux/store'
import {
    getStoreInfo,
    isConfigured,
} from '@atredux/selectors/storeSelector'
import { IUserData } from '@utils/UserData'

// Having defaults for prods can lead to compatibility issue to migrate to the new stack when it is setup for IIB for that region
const IIBClientIds = {
    test: {
        default: '3gpgb9cjd2mftr9if53cbitrvj',
    },
    gamma: {
        default : '3rlfii3q65bci2djku5k6qiogb',
    },
    prod : {
        iad : '5ain6580512epc57crb3fek9at',
        pdx : '4no4s3po212g591ubhko1svv15',
        dub : '11hl2q11bf78vqutujsgftdda1',
        sin : '13ilik0m0hlnsl0f65j9hqb87',
    },
}

// DorothyUtils returns availabe use types as ['amazon-employee', 'asset-user', 'wfm-employee']
// Making the conversion here instead of DorothyUtils to maintain current logic/keep IIB changes isolated
const LibbyUserTypeToIIBUserType = {
    'amazon-employee': 'amazon',
    'asset-user': '3p',
    'wfm-employee': 'wfm' ,
}

const azureLogout = 'https://login.microsoftonline.com/common/oauth2/logout'

const IIBLoginVips = {
    test: {
        default : 'https://beta.identitybroker.ihm.amazon.dev/associatePlatform',
    },
    gamma: {
        default : 'https://gamma.us-east-1.identitybroker.ihm.amazon.dev/associatePlatform',
    },
    prod : {
        iad : 'https://prod.us-east-1.identitybroker.ihm.amazon.dev/associatePlatform',
        pdx : 'https://prod.us-west-2.identitybroker.ihm.amazon.dev/associatePlatform',
        dub : 'https://prod.eu-west-1.identitybroker.ihm.amazon.dev/associatePlatform',
        sin : 'https://prod.ap-southeast-1.identitybroker.ihm.amazon.dev/associatePlatform',
    },
}

const IIBAuthVips = {
    test: {
        default : 'https://beta-iib.auth.us-west-2.amazoncognito.com',
    },
    gamma: {
        default : 'https://gamma-iib.auth.us-east-1.amazoncognito.com',
    },
    prod : {
        iad : 'https://prod-iib.auth.us-east-1.amazoncognito.com',
        pdx : 'https://prod-iib.auth.us-west-2.amazoncognito.com',
        dub : 'https://prod-iib.auth.eu-west-1.amazoncognito.com',
        sin : 'https://prod-iib.auth.ap-southeast-1.amazoncognito.com',
    },
}

export class IIBServiceClient {
    public static IIB_STATE_LOCAL_STORAGE_KEY = 'iibState'
    public static readonly RETRY_BACKOFF_CAP = 5000 // maximum backoff is 5s
    public static readonly RETRY_BACKOFF_BASE = 1500 // first retry in 3s, second in 10s
    private static readonly MAX_RETRY_ATTEMPTS = 3
    private static readonly WFM_DOMAIN = '@wholefoods.com'
    // Fetches the config for the region and domain based on config provided
    // configs are nested json structred  domain and then region
    public static getRegionConfigs (configs,configname): string {
        const region = (StoreIdService.getSelectedStoreIdRegion() || EnvironmentDetail.getRegion() || '').toLowerCase()
        const domain = (EnvironmentDetail.getDomain() || '').toLowerCase()
        console.log('ATBase fetching IIB',configname,' config for domain,region :- ',domain,region)
        if (configs[domain]) {
            if (configs[domain] && configs[domain][region]) {
                return configs[domain][region]
            }
            if (configs[domain].default) {
                return configs[domain].default
            }
        }
        return ''
    }

    public static getAssociateToolsBaseUrl (): string {
        const absUrl = window.location.href

        return absUrl.split('?')[0]
    }

    public static getLocale (): string {
        return getParameterByName('locale')
    }

    public static getUriParamsWithEnvironmentDetails (): string {
        const amazonDomain = EnvironmentDetail.getDomain()
        const region = (StoreIdService.getSelectedStoreIdRegion() || EnvironmentDetail.getRegion())
        IIBServiceClient.storeParamsInSessionStorage()
        let result = 'auth%3Diib'
        if (amazonDomain) {
            result += '%26domain%3D' + amazonDomain
        }
        if (region) {
            result += '%26region%3D' + region
        }
        return result
    }

    /**
     * Creates a URL that can be used to sign in to get the auth code.
     */
    public static async getIIBSignInUrlForAuthFlow (): Promise<string> {

        const iIBVip = IIBServiceClient.getRegionConfigs(IIBLoginVips,'IIBLoginVips')
        const clientId = IIBServiceClient.getRegionConfigs(IIBClientIds,'IIBClientIds')
        const state = UUIDUtils.generate()
        const associateToolsBaseUrl = IIBServiceClient.getAssociateToolsBaseUrl()
        const storeId = EnvironmentDetail.getStoreId()
        const redirectUri = associateToolsBaseUrl + '?' + IIBServiceClient.getUriParamsWithEnvironmentDetails()
        console.log('ATBase: Redirect Sign URI for IIB call: ', redirectUri)
        window.localStorage.setItem(
            IIBServiceClient.IIB_STATE_LOCAL_STORAGE_KEY, state)
        let endpointPath
        let userType
        let clientID3P

        // need to pull ClientID from loginType params in window for 3p to pass onto IIBWebsite
        const setClientID = (loginType) => {
            if (loginType !== 'amazon' && loginType !== 'vendor' && loginType !== 'wfm') {
                clientID3P = loginType
            }
        }
        if (UserTypeService.getUserType()) {
            userType = LibbyUserTypeToIIBUserType[UserTypeService.getUserType().toLowerCase()]
            if (userType === '3p') {
                const availableloginTypes = getParameterByName('loginType').split('_')
                availableloginTypes.forEach(setClientID)
                clientID3P ? endpointPath = clientID3P : console.error('3p selected but no ClientID found in loginTypes')
            } else {
                endpointPath = userType
            }
        } else {
            endpointPath = EnvironmentDetail.getLoginType()
        }
        const url = iIBVip + '/' + endpointPath + '?client_id=' + clientId + '&state=' + state + '&redirect_uri=' + redirectUri + '&storeId=' + storeId
        const urlWithLoginHint = await IIBServiceClient.appendLoginHint(url, endpointPath)
        console.log('ATBase: IhmIdentityBroker URL line: ', urlWithLoginHint)
        return urlWithLoginHint
    }

    /**
     * Calls IIB's endpoint to retrieve the token using the refresh token (from previous step).
     */
    public static async refreshTokens (): Promise<any> {
        let retryAttempt: number = 0
        let tokensReceived: Boolean = false
        const IIBAuthvip = IIBServiceClient.getRegionConfigs(IIBAuthVips,'IIBAuthVips')
        const clientId = IIBServiceClient.getRegionConfigs(IIBClientIds,'IIBClientIds')
        const storeId = StoreIdService.getSelectedStoreId() || EnvironmentDetail.getStoreId()
        const associateToolsBaseUrl = IIBServiceClient.getAssociateToolsBaseUrl()
        const redirectUri = associateToolsBaseUrl + '?' + IIBServiceClient.getUriParamsWithEnvironmentDetails()
        console.log('ATBase: Refresh Redirect URI for refresh IIB call: ', redirectUri)
        const refreshToken = await IIBTokenProvider.getRefreshToken()
        const url = IIBAuthvip + '/oauth2/token?client_id=' + clientId + '&redirect_uri=' + redirectUri + '&grant_type=refresh_token' + '&refresh_token=' + refreshToken
        const headers: HeadersInit = {
            'Content-Type': 'application/x-www-form-urlencoded',
        }

        console.log('ATBase: Refresh Making request to get token info from URL: ', url)
        const refreshAuthTokenLatency = new LatencyMetric(storeId, 'IIBAuth', 'IhmFlyingDutchman', 'refreshAuthToken.Latency')
        const refreshAuthTokenSuccess = new CountMetric(storeId, 'IIBAuth', 'IhmFlyingDutchman', 'refreshAuthToken.Success')
        const refreshAuthTokenError = new CountMetric(storeId, 'IIBAuth', 'IhmFlyingDutchman', 'refreshAuthToken.Error')
        refreshAuthTokenLatency.start()
        do {
            console.log('ATBase: Refresh Making call to IIB to get tokens using refresh token attempt: ',retryAttempt)
            const backoff = Math.min(
                IIBServiceClient.RETRY_BACKOFF_CAP,
                IIBServiceClient.RETRY_BACKOFF_BASE * 2 ** retryAttempt)
            const jitterMs = _.random(0, backoff)
            IIBServiceClient.timeDelay(jitterMs)
            const fetchPromise = fetch(url, {
                method: 'POST',
                headers,
            })
            await fetchPromise.then((response) => {
                console.log('ATBase: Refresh Successful call to IIB endpoint for refresh token.')
                refreshAuthTokenLatency.stop()
                refreshAuthTokenSuccess.count(1)
                refreshAuthTokenError.count(0)
                return response.json()
            }).then((iibData) => {
                console.log('ATBase: Refresh Successful IIB authentication with refresh token.')
                tokensReceived = true
                IIBTokenProvider.setToken(iibData.access_token, refreshToken,iibData.id_token,iibData.token_type,iibData.expires_in)
            }).catch((exception) => {
                console.error('ATBase: Unsuccessful call to IIB endpoint with refresh token.')
                refreshAuthTokenLatency.stop()
                refreshAuthTokenSuccess.count(0)
                refreshAuthTokenError.count(1)
                return Promise.reject(exception)
            })
            retryAttempt++
        } while (retryAttempt < IIBServiceClient.MAX_RETRY_ATTEMPTS && !tokensReceived)
    }

    public static timeDelay = async (ms) => {
        await new Promise((resolve) => setTimeout(resolve, ms))
    }

    // This method will construct and return the IIB logout URL which should be navigated to by the browser.
    // Once the logout is done, the webview should be redirected back to the IHM login page.
    // param {boolean} rememberUsername boolean value for `keep_username` query parameter.
    // return {string} IIB logout url string.
    public static async getLoginUrl (): Promise<string> {
        // TODO_IIB add logic to call to invalidate the tokens
        const signInUrl = await IIBServiceClient.getIIBSignInUrlForAuthFlow()
        return signInUrl
    }

    public static async login (): Promise<void> {
        console.log('ATBase: IIB login()')
        const loginUrl = await IIBServiceClient.getLoginUrl()
        console.log('ATBase: IIB Client navigating to  login endpoint', loginUrl)
        window.top.location.assign(loginUrl)
        return Promise.resolve()
    }

    public static logout (): void {
        const IIBAuthvip = IIBServiceClient.getRegionConfigs(IIBAuthVips,'IIBAuthVips')
        const clientId = IIBServiceClient.getRegionConfigs(IIBClientIds,'IIBClientIds')
        const storeId = StoreIdService.getSelectedStoreId() || EnvironmentDetail.getStoreId()
        const bypassLogoutDorothy = (isConfigured(store.getState()) && getStoreInfo(store.getState()).isDorothyEnabled) || (!!DorothyUtils.getDorothyStoreInfo(storeId))
        let redirectUri
        if (UserTypeService.getIsWfmLogin()) {
            const associateToolsBaseUrlEncoded = encodeURIComponent(IIBServiceClient.getAssociateToolsBaseUrl())
            let uriParamsEncoded = '%3F' + IIBServiceClient.getUriParamsWithEnvironmentDetails()
            if (bypassLogoutDorothy) {
                uriParamsEncoded = uriParamsEncoded + '%23user-type-selection'
            }
            redirectUri = encodeURIComponent(azureLogout + '?post_logout_redirect_uri=') + encodeURIComponent(associateToolsBaseUrlEncoded + uriParamsEncoded)
        } else {
            let uriParams = IIBServiceClient.getUriParamsWithEnvironmentDetails()
            if (bypassLogoutDorothy) {
                uriParams = uriParams + '#user-type-selection'
            }
            const associateToolsBaseUrl = IIBServiceClient.getAssociateToolsBaseUrl()
            redirectUri = associateToolsBaseUrl + '?' + uriParams
        }
        new CountMetric(storeId, 'IIBAuth', 'IhmFlyingDutchman', 'logout.Request').count(1)
        console.log('ATBase: Redirect URI for IIB call to logout user: ', redirectUri)
        const url = IIBAuthvip + '/logout?client_id=' + clientId + '&logout_uri=' + redirectUri
        console.log('ATBase: Making request to logout user from IIB: ', url)
        window.location.replace(url)
    }
    /**
     * Calls IIB's endpoint to retrieve the token using the code (extracted from the URL).
     * This requires relies on retrieving the redirect url and the state from session storage function.
     *
     * return {Promise} Returns a an object with the tokens with success.
     *  Successful response example:
     *      {
     *          accessToken: {String}, // access token for the login.
     *          tokenExpiry: {number}, // Time in seconds until the token expires.
     *          idToken: {String}, // Id token for the logged in user used to call backend services.
     *          tokenType: {String} // The token type (usually "Bearer").
     *          refreshToken: {string} // the token to retrieve new tokens when they expire
     *      }
     */
    public static retrieveAuthToken (): Promise<any> {
        const IIBAuthvip = IIBServiceClient.getRegionConfigs(IIBAuthVips,'IIBAuthVips')
        const clientId = IIBServiceClient.getRegionConfigs(IIBClientIds,'IIBClientIds')
        const code = getParameterByName('code')
        const associateToolsBaseUrl = IIBServiceClient.getAssociateToolsBaseUrl()
        const redirectUri = associateToolsBaseUrl + '?' + IIBServiceClient.getUriParamsWithEnvironmentDetails()
        console.log('ATBase IIB TOKEN: Redirect URI for IIB call: ', redirectUri)
        const url = IIBAuthvip + '/oauth2/token?client_id=' + clientId + '&redirect_uri=' + redirectUri + '&grant_type=authorization_code' + '&code=' + code
        const headers: HeadersInit = {
            'Content-Type': 'application/x-www-form-urlencoded',
        }
        console.log('ATBase IIB TOKEN: Making request to get IIB token info from URL: ', url)
        return fetch(url, {
            method: 'POST',
            headers,
        }).then((response) => {
            console.log('ATBase IIB TOKEN: Successful call to IIB endpoint.')
            return response.json()
        }).catch((exception) => {
            console.error('ATBase IIB TOKEN: Unsuccessful call to IIB endpoint.')
            return Promise.reject(exception)
        })
    }

    // IIB has strict redirect uri checking and hence the essential params are stored in session storage.
    private static storeParamsInSessionStorage (): void {
        const initialTool = EnvironmentDetail.getInitialTool()
        const initialZoneInfo = EnvironmentDetail.getInitialZoneInfo()
        const suppressConnections = EnvironmentDetail.getSuppressConnections()
        const storeId = StoreIdService.getSelectedStoreId() || EnvironmentDetail.getStoreId()
        window.sessionStorage.setItem('storeId', storeId)
        window.sessionStorage.setItem('loginType',EnvironmentDetail.getLoginType())
        if (initialTool) {
            window.sessionStorage.setItem('initialTool',initialTool)
        }
        if (initialZoneInfo) {
            window.sessionStorage.setItem('initialZoneName',initialZoneInfo.zoneName)
            window.sessionStorage.setItem('initialSubZoneName',initialZoneInfo.subzoneName)
        }
        if (suppressConnections) {
            window.sessionStorage.setItem('suppressConnections',suppressConnections)
        }
    }

    private static async appendLoginHint (url: string, endpointPath: string): Promise<string> {
        try {
            const userData: IUserData = await IIBTokenProvider.retrieveUserDataFromSharedPref()
            if (!userData?.userId) {
                console.log('ATBase: No userId found in stored data')
                return url
            }
            const isWfmEndpoint = endpointPath.includes('wfm')
            const userName = isWfmEndpoint
                ? `${userData.userId}${this.WFM_DOMAIN}` : userData.userId
            console.log('ATBase: lastLoggedIn Username: ', userName)
            return `${url}&login_hint=${userName}`
        } catch (error) {
            console.error('ATBase: Failed to retrieve user data:', error)
            return url
        }
    }
}
